import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Search, Eye } from "lucide-react";

export default function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const fetch = React.useCallback(()=>{
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status && status !== "all") p.set("status_f", status);
    api.get(`/admin/applications?${p.toString()}`).then(r=>setRows(r.data));
  },[q, status]);
  useEffect(()=>{ fetch(); },[fetch]);
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Pipeline</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-1">Applications</h1>
          <p className="text-slate-400 mt-1">{rows.length} record{rows.length!==1?"s":""}</p>
        </div>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-4 mb-6">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="flex items-center gap-2 bg-slate-800 rounded-md px-3">
            <Search className="w-4 h-4 text-slate-400"/>
            <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search candidate, job, ID..." className="border-0 bg-transparent text-white focus-visible:ring-0" data-testid="admin-apps-search"/>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="admin-apps-status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="assessment_pending">Assessment Pending</SelectItem>
              <SelectItem value="submitted">Submitted / Under Review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Application ID</th>
                <th className="text-left px-4 py-3">Job</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Assessment</th>
                <th className="text-left px-4 py-3">Applied</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map(a=>(
                <tr key={a.id} className="hover:bg-slate-800/50" data-testid={`admin-app-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{a.candidate_name}</div>
                    <div className="text-xs text-slate-400">{a.candidate_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">{a.id}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{a.job_title}</div>
                    <div className="text-xs text-slate-400">{a.department} · {a.location}</div>
                  </td>
                  <td className="px-4 py-3"><Badge className="bg-slate-700 text-white border-none hover:bg-slate-700">{a.stage}</Badge></td>
                  <td className="px-4 py-3">
                    {a.assessment_status === "completed" ? (
                      <Badge className={`border-none hover:bg-emerald-500/20 ${a.assessment_score >= 60 ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>{a.assessment_score}%</Badge>
                    ) : (
                      <Badge className="bg-amber-500/20 text-amber-400 border-none hover:bg-amber-500/20">Pending</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Button asChild size="sm" variant="ghost" className="text-[#F59E0B] hover:bg-slate-800 hover:text-[#F59E0B]" data-testid={`admin-app-view-${a.id}`}>
                      <Link to={`/admin/applications/${a.id}`}><Eye className="w-4 h-4 mr-1"/>View</Link>
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-500">No applications found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  );
}
